import { useEffect, useMemo, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AcademyLogsApi from 'libs/api/retailAcademy/academyLogs';
import { useTableChangeFilterHook } from 'hooks/useTableChangeFilterHook';
import { useFilter } from 'hooks/useFilter';
import { useRecoilValue } from 'recoil';
import { getShoppingCenters } from 'hooks/useUser';
import { academyLogsAdapter } from './academyLogs.adapter';
import { selectYears, selectMarcoArea } from '../../contants';
import AcademyArticlesApi from 'libs/api/retailAcademy/academyArticles';
import Helpers from 'helpers';
import UIHelper from 'helpers/UIHelper';
import AcademyCategoriesApi from 'libs/api/retailAcademy/academyCategories';
import { fileNameEdit } from 'helpers/FileUtils';


const api = new AcademyLogsApi();
const academyArticlesApi = new AcademyArticlesApi();
const academyCategoriesApi = new AcademyCategoriesApi();

const defaultState = {
  retailLcademyLogs: [],
  pagination: {
    current: 1,
    pageSize: 30,
    position: ['bottomCenter'],
    hideOnSinglePage: true,
    showSizeChanger: false,
  },
  sorting: {
    field: '',
    order: '',
  },
};

const useLogs = ({ isMainPage = true } = {}) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const [data, setData] = useState(defaultState);
  const [loading, setLoading] = useState(false);
  const onChangeTable = useTableChangeFilterHook();
  const { searchParams } = useFilter();
  const shoppingCenters = useRecoilValue(getShoppingCenters);
  const [academyArticles, setAcademyArticles] = useState([]);
  const [academyCategories, setAcademyCategories] = useState([]);

  const columns = useMemo(() => {
    return [
      {
        title: t('Outlet Village'),
        dataIndex: 'shopping_centers.title',
        fixed: 'left',
        sorter: () => {},
      },
      {
        title: t('Utente'),
        dataIndex: 'users.name',
        sorter: () => {},
      },
      {
        title: t('Macro-Area'),
        dataIndex: 'academy_articles.macro_area',
        sorter: () => {},
      },
      {
        title: t('Anno Edizione'),
        dataIndex: 'academy_categories.year',
        sorter: () => {},
      },
      {
        title: t('Modulo'),
        dataIndex: 'academy_categories.title',
        sorter: () => {},
      },

      {
        title: t('Capitoli'),
        dataIndex: 'academy_articles.title',
        sorter: () => {},
      },
      {
        title: t('Visualizzazioni'),
        dataIndex: 'count_show',
        sorter: () => {},
      },
    ];
  }, [pathname, t]);

  const filters = useMemo(() => {
    return [
      {
        title: t('Outlet Village'),
        type: 'select',
        name: 'by_shopping_center',
        values: shoppingCenters.map((i) => ({ value: i.id, title: i.title })),
      },
      {
        title: t('Macro-Area'),
        type: 'select',
        name: 'by_macro_area',
        values: selectMarcoArea.map((i) => ({ value: i, title: i })),
      },
      {
        title: t('Anno Edizione'),
        type: 'select',
        name: 'by_year',
        values: selectYears.map((i) => ({ value: i, title: i })),
      },
      {
        title: t('Modulo'),
        type: 'select',
        name: 'by_academy_category',
        values: academyCategories.map((i) => ({
          value: i.id,
          title: i.title,
        })),
      },
      {
        title: t('Utente'),
        type: 'input',
        name: 'by_user_text',
        values: [],
      },
      {
        title: t('Capitolo'),
        type: 'select',
        name: 'by_academy_article',
        values: academyArticles.map((i) => ({ value: i.id, title: i.title })),
      },
    ];
  }, [shoppingCenters, academyArticles, t, academyCategories]);

  useEffect(() => {
    (async () => {
      try {
        const params = {
          per: 10000,
        };
        const {
          data: { academy_categories = [] },
        } = await academyCategoriesApi.list({ params });
        setAcademyCategories(academy_categories);
      } catch (e) {
        console.log(e);
      }
    })();
  }, [isMainPage]);

  const handleGet = async () => {
    setLoading(true);
    const currentParams = Object.fromEntries([...searchParams]);
    try {
      const params = {
        per: data.pagination.pageSize,
        page: data.pagination.current,
        ...currentParams,
      };
      if (data.sorting.order) {
        params.sortable_column = data.sorting.field;
        params.direction = data.sorting.order;
      }
      const {
        data: { retail_academy_logs = [], pagination = { total_count: 0 } },
      } = await api.list({ params });
      setData((s) => ({
        ...s,
        retailLcademyLogs: academyLogsAdapter(retail_academy_logs),
        pagination: {
          ...defaultState.pagination,
          ...pagination,
          total: pagination?.total_count,
        },
      }));
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadExcel = async () => {
    try {
      const currentParams = Object.fromEntries([...searchParams]);
      const params = {
        ...currentParams,
      };
      if (data.sorting.order) {
        params.sortable_column = data.sorting.field;
        params.direction = data.sorting.order;
      }

      const response = await api.getExcel({ params });
      Helpers.downloadFile(response, fileNameEdit());
    } catch (e) {
      UIHelper.serverError();
    }
  };

  useEffect(() => {
    if (isMainPage) {
      (async () => {
        await handleGet();
      })();
    }
  }, [data.pagination.current, data.sorting.order, isMainPage, searchParams]);

  const onChangeTableFilter = useCallback(
    (paginate, sort, filter) => {
      onChangeTable(setData, paginate, sort, filter);
    },
    [onChangeTable],
  );

  useEffect(() => {
    (async () => {
      if (isMainPage) {
        try {
          const params = {
            per: 10000,
          };
          const {
            data: { academy_articles = [] },
          } = await academyArticlesApi.list({ params });
          setAcademyArticles(academy_articles);
        } catch (e) {
          console.log(e);
        }
      }
    })();
  }, [isMainPage]);

  return {
    columns,
    data,
    filters,
    onChangeTableFilter,
    loading,
    handleDownloadExcel,
  };
};

export default useLogs;
